import { CSSProperties, useState } from 'react';
import classNames from 'classnames';
import ButtonCube from '#components/controls/buttons/buttonCube/ButtonCube';
import {
  EmailShareButton,
  EmailIcon,
  FacebookShareButton,
  FacebookIcon,
  TwitterShareButton,
  TwitterIcon,
} from 'react-share';
import { copyToClipboard } from '#utils';
import s from '#components/controls/buttons/buttonShare/buttonShare.module.scss';
import { staticTitle } from '#config';
import logger from '#utils/logger';
import useTranslation from 'next-translate/useTranslation';

type Props = {
  title: string;
  description: string;
  uri: string;
  extraClass?: string;
  isSizeSmall?: boolean;
};

const ButtonShare = ({
  title,
  description,
  uri,
  extraClass,
  isSizeSmall = false,
}: Props) => {
  const { t } = useTranslation();
  const blockClass = classNames(s.host, extraClass, {
    [s.isSizeSmall]: !!isSizeSmall,
  });
  const [isShowingSocialIcons, setIsShowingSocialIcons] = useState(false);
  const [clipboardIcon, setClipboardIcon] = useState('clipboard');

  const onClick = (e) => {
    e.preventDefault();
    try {
      navigator
        .share({
          title: title,
          text: description,
          url: uri,
        })
        .catch(() => {
          logger.info('abborted by user');
        });
    } catch (error) {
      setIsShowingSocialIcons(!isShowingSocialIcons);
    }
  };

  const shareButtonInlineStyle: CSSProperties = {
    position: 'relative',
    opacity: isShowingSocialIcons ? 1 : 0,
    pointerEvents: isShowingSocialIcons ? 'all' : 'none',
    transitionDuration: '300ms',
  };

  const transientIconForClipboard = (icon, time) => {
    setClipboardIcon(icon);
    setTimeout(() => setClipboardIcon('clipboard'), time);
  };

  const handleClipboardButton = (e) => {
    e.preventDefault();
    copyToClipboard(uri)
      .then(() => {
        transientIconForClipboard('check', 5000);
      })
      .catch(() => {
        transientIconForClipboard('x', 5000);
      });
  };

  // update
  const transitionDelay = (i, isOpening) => {
    const n = 3; // n = share elements - 1
    return isOpening ? `${i * 100}ms` : `${(n - i) * 100}ms`;
  };

  if (!title) {
    return null;
  }

  return (
    <>
      <button
        className={blockClass}
        type="button"
        title={title}
        onClick={onClick}
      >
        <span className={s.shareIcon} />
      </button>
      <EmailShareButton
        className={s.shareOptionItem}
        subject={`${staticTitle}${t('emailShareTitle')} ${title}!`}
        body={description}
        separator={` - ${t('emailShareBody')}: `}
        url={uri}
        style={{
          ...shareButtonInlineStyle,
          transitionDelay: transitionDelay(0, isShowingSocialIcons),
        }}
      >
        <EmailIcon
          size={isSizeSmall ? '30px' : '42px'}
          bgStyle={{ fill: 'rgba(0, 0, 0, 0)' }}
        />
        <div className={s.buttonBackground} />
      </EmailShareButton>
      <FacebookShareButton
        className={s.shareOptionItem}
        quote={`${staticTitle}${title} – ${description}`}
        hashtag="veejoy"
        url={uri}
        style={{
          ...shareButtonInlineStyle,
          transitionDelay: transitionDelay(1, isShowingSocialIcons),
        }}
      >
        <FacebookIcon
          size={isSizeSmall ? '30px' : '42px'}
          bgStyle={{ fill: 'rgba(0, 0, 0, 0)' }}
        />
        <div className={s.buttonBackground} />
      </FacebookShareButton>
      <TwitterShareButton
        className={s.shareOptionItem}
        title={`${staticTitle}${title}`}
        hashtags={['veejoy', title.replace(' ', '')]}
        related={['@europapark']}
        url={uri}
        style={{
          ...shareButtonInlineStyle,
          transitionDelay: transitionDelay(2, isShowingSocialIcons),
        }}
      >
        <TwitterIcon
          size={isSizeSmall ? '30px' : '42px'}
          bgStyle={{ fill: 'rgba(0, 0, 0, 0)' }}
        />
        <div className={s.buttonBackground} />
      </TwitterShareButton>
      <ButtonCube
        extraClass="small:tw-hidden"
        icon={clipboardIcon}
        onClick={handleClipboardButton}
        style={{
          ...shareButtonInlineStyle,
          transitionDelay: transitionDelay(3, isShowingSocialIcons),
        }}
      />
    </>
  );
};

export default ButtonShare;
